// @flow
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
// Components
import { PageLoader, OutOfStockNotice } from '../../common';
import {
  ProductMostSoldScroller,
  ProductRecentlyViewedScroller,
} from '../../products';
import TotalAvailableStoreCredit from '../../user/containers/TotalAvailableStoreCredit';
import EmptyCart from '../images/empty-cart.png';
import ItemList from './ItemList';
import BlendList from './BlendList';
import CartPanel from './CartPanel';
import CartResumePreview from './CartResumePreview';
// Analytics
import * as Analytics from '../../common/analytics';
// Types
import type {
  FeaturesConfig,
  BigClubCashbackSettings,
} from '../../preferences/types';
import type { BigClubInfo, LineItem } from '../types';
// Styles
import './Cart.css';
//Helpers
import { useScreenTimeLogger } from '../../common';
import CheckoutErrors from './CheckoutErrors';

type Props = {
  items: LineItem[],
  totalPrice: number,
  numberOfItems: number,
  totalKg: number,
  totalUnit: number,
  bigClubInfo?: BigClubInfo,
  handleInitiateCheckout: () => void,
  handleRemoveItem: (lineItemId: number) => void,
  handleRemoveBlend: (lineItemId: number) => void,
  loading: boolean,
  featuresConfig: FeaturesConfig,
  cashbackSettings: BigClubCashbackSettings,
  generalErrors: string[],
  dismissErrors: () => void,
  orderNumber: ?string,
  orderToken: ?string,
  userId: ?number,
};

const Cart = ({
  blends,
  loading,
  numberOfItems,
  items,
  totalKg,
  totalUnit,
  totalPrice,
  handleRemoveItem,
  handleRemoveBlend,
  removingItemLoading,
  bigClubInfo,
  featuresConfig,
  cashbackSettings,
  promotions,
  handleInitiateCheckout,
  generalErrors,
  dismissErrors,
  orderNumber,
  orderToken,
  userId,
}: Props) => {
  const outOfStockItems = _.filter(items, l => l.variant.inStock === false);
  const discontinuedItems = _.filter(
    items,
    l => l.variant.discontinued === true,
  );

  const hasProductsDiscontinuedOrOutOfStock =
    !!outOfStockItems.length || !!discontinuedItems.length;

  const cashbackMinOrderPriceToEarn =
    cashbackSettings?.cashbackMinOrderPriceToEarn;

  // USE ANALYTICS
  useScreenTimeLogger(window.location.pathname);
  useEffect(() => {
    if (items && items.length > 0) {
      items.forEach(item => {
        Analytics.logLineItemView(item, window.location.pathname, 'cart');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveItemAction = lineItemId => {
    const lineItem = _.find(items, item => item.id === lineItemId);
    Analytics.logLineItemRemove(lineItem, window.location.pathname, 'cart');
    handleRemoveItem(lineItemId);
  };

  const handleRemoveBlendAction = blendId => {
    const blend = _.find(blends, b => b.id === blendId);
    Analytics.logLineItemRemove(blend, window.location.pathname, 'cart_blend');
    handleRemoveBlend(blendId);
  };

  const errorsRef = useRef(null);

  const handleCheckoutFail = () => {
    if (!errorsRef.current) return;

    const rect = errorsRef.current.getBoundingClientRect();
    const fullyVisible =
      rect.top >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight);

    if (!fullyVisible) {
      errorsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (errorsRef.current.focus) {
      errorsRef.current.focus({ preventScroll: true });
    }
  };

  return (
    <div className="cart-page-content-main-container">
      {loading && numberOfItems === 0 ? (
        <PageLoader />
      ) : numberOfItems === 0 ? (
        <div className="cart-page-empty">
          <h3>Seu carrinho está vazio. Vamos enchê-lo?</h3>
          <img src={EmptyCart} alt="" />
        </div>
      ) : (
        <div>
          <div className="cart-page-out-of-stock-notice">
            <OutOfStockNotice
              shouldDisplay={hasProductsDiscontinuedOrOutOfStock}
            />
          </div>
          <div className="cart-page-main-container">
            <div className="cart-page-left-container">
              <div ref={errorsRef} tabIndex="-1">
                <CheckoutErrors
                  errors={generalErrors}
                  dismiss={dismissErrors}
                  showErrorFields={false}
                />
              </div>
              <CartPanel
                numberOfItems={numberOfItems}
                totalCartPrice={totalPrice}
                bigClubInfo={bigClubInfo}
                minPriceToEarnCashback={cashbackMinOrderPriceToEarn}
                shouldDisplayFreeShippingNotice={totalPrice < 149}
                shouldDisplayBigClubNotice={featuresConfig?.cashbackFeature}
                promotions={promotions}
              />
              <ItemList
                items={items}
                handleRemoveItem={handleRemoveItemAction}
                removingItemLoading={removingItemLoading}
                errors={generalErrors}
                loading={loading && numberOfItems > 0}
              />
              <BlendList
                blends={blends}
                handleRemoveBlend={handleRemoveBlendAction}
                loading={loading && numberOfItems > 0}
              />
            </div>
            <div className="cart-page-right-container">
              <CartResumePreview
                totalPrice={totalPrice}
                hasProductsDiscontinuedOrOutOfStock={
                  hasProductsDiscontinuedOrOutOfStock
                }
                items={items}
                handleInitiateCheckout={handleInitiateCheckout}
                onCheckoutFail={handleCheckoutFail}
                orderNumber={orderNumber}
                orderToken={orderToken}
                userId={userId}
              />
            </div>
          </div>
        </div>
      )}
      <TotalAvailableStoreCredit />
      <ProductRecentlyViewedScroller />
      <ProductMostSoldScroller />
    </div>
  );
};

export default Cart;
